import { cloneDeep, countBy, isEqual, isInteger, mapValues } from 'lodash';

import * as Game from './Game';

const { abs, max, min, floor, ceil, round } = Math;

export const performAction = (state: Game.State, action: Game.Action): Game.State | null => {
	switch (action.type) {
		case Game.ActionType.StartGame: {
			if (state.type !== 'preparation')
				return null;

			if (!isEqual(
				new Set(Object.keys(state.users)),
				new Set(Object.keys(action.positions))
			))
				return null;

			const next: Game.PlayingState = {
				...state,
				
				// Dangerous, start game shouldn't be called from player directly
				...action.parameters, 
				
				type: 'started',

				users: mapValues(state.users, (user, uid) => ({
					...user,
					health: action.health,
					actionPoints: action.actionPoints,
					range: action.range,
					pos: action.positions[uid],

					votesReceived: 0,
					votesAvailable: 0,
				})),

				startedAt: action.startedAt,
				initialScheduleAt: action.initialScheduleAt,

				// width: action.width,
				// height: action.height,

				// scheduleInterval: action.scheduleInterval,

				// rangeUpgradeCost: action.rangeUpgradeCost,
				// healCost: action.healCost,
			};

			return next;
		}

		case Game.ActionType.ResetGame: {
			const next: Game.PreparationState = {
				type: 'preparation',

				name: state.name,
				public: state.public,
				ownerId: state.ownerId,
				createdAt: state.createdAt,

				users: mapValues(state.users, u => ({
					joinedAt: u.joinedAt,
					nickname: u.nickname,
					status: u.status,
					info: u.info,
				})),
			};

			return next;
		}

		case Game.ActionType.Schedule: {
			if (state.type != 'started')
				return null;

			const next: Game.PlayingState = {
				...state,
				users: mapValues(
					state.users,
					(u: Game.Player): Game.Player =>
					u.health > 0
					? ({
						...u,
						actionPoints: u.actionPoints
							+ state.scheduleActionPointsPerAlive
							+ floor(u.votesReceived / state.scheduleVotesToActionPoints),
						votesReceived: 0,
					})
					: ({
						...u,
						votesAvailable: state.scheduleVotesPerDead, // votes don't accumulate
					})
				),
			};

			return next;
		}

		case Game.ActionType.SetStatus: {
			if (action.status.length > 200)
				return null; // Status too long

			const next = cloneDeep(state);
			next.users[action.actorId].status = action.status;
			return next;
		}

		case Game.ActionType.Move: {
			if (state.type != 'started')
				return null;

			if (!zeroOrMore(action.to.x) || action.to.x >= state.width ||
				!zeroOrMore(action.to.y) || action.to.y >= state.height)
				return null;

			const actor = state.users[action.actorId];
			const dist = distance(actor.pos, action.to);
			if (dist > actor.actionPoints)
				return null;

			// I wanted to use isEqual,
			// but in this case user could supply extra fields
			// thus making the objects not equal in not-meaningful way
			// but formally not equal, and consequently occupy position
			if (Object.values(state.users).some(u => posEqual(u.pos, action.to)))
				return null;

			const next: Game.PlayingState = {
				...state,
				users: {
					...state.users,
					[action.actorId]: {
						...actor,
						pos: action.to,
						actionPoints: actor.actionPoints - dist,
					}
				}
			};

			return next;
		}

		case Game.ActionType.Shoot: {
			if (state.type != 'started')
				return null;

			if (action.actorId == action.shootedPlayerId)
				return null;

			if (!oneOrMore(action.count))
				return null;

			const actor = state.users[action.actorId];
			if (actor.actionPoints < action.count)
				return null;
			
			const shooted = state.users[action.shootedPlayerId];
			if (!isEqual(shooted.pos, action.to))
				return null;
			
			// ?? Should we automatically shoot player for less damage
			if (shooted.health < action.count)
				return null;

			const dist = distance(actor.pos, shooted.pos);
			if (dist > actor.range)
				return null;

			const killed = shooted.health == action.count;
			const stolenActionPoints = killed ? shooted.actionPoints : 0;

			const next: Game.PlayingState = {
				...state,
				users: {
					...state.users,
					[action.actorId]: {
						...actor,
						actionPoints: actor.actionPoints - action.count + stolenActionPoints,
					},
					[action.shootedPlayerId]: {
						...shooted,
						health: shooted.health - action.count,
						actionPoints: shooted.actionPoints - stolenActionPoints,
					},
				},
			};

			const aliveUsers = Object.values(next.users).filter(u => u.health > 0).length;
			if (aliveUsers <= 1) {
				next.type = 'finished';
			}

			return next;
		}

		case Game.ActionType.Transfer: {
			if (state.type != 'started')
				return null;

			if (action.actorId == action.receiverId)
				return null;

			if (!zeroOrMore(action.actionPoints)
				|| !zeroOrMore(action.healthPoints)
				|| (action.actionPoints == 0 && action.healthPoints == 0))
				return null;
			
			const actor = state.users[action.actorId];
			if (actor.actionPoints < action.actionPoints
				|| actor.health < action.healthPoints)
				return null;

			const receiver = state.users[action.receiverId];

			// Can transfer actionPoints and healthPoints to dead players,
			// but have to revive them by transferring at least one HP
			if (receiver.health <= 0
				&& action.healthPoints == 0)
				return null;

			const dist = distance(actor.pos, receiver.pos);
			if (dist > actor.range)
				return null;

			const next: Game.PlayingState = {
				...state,
				users: {
					...state.users,
					[action.actorId]: {
						...actor,
						actionPoints: actor.actionPoints - action.actionPoints,
						health: actor.health - action.healthPoints,
					},
					[action.receiverId]: {
						...receiver,
						actionPoints: receiver.actionPoints + action.actionPoints,
						health: receiver.health + action.healthPoints,
						votesAvailable: 0,
						// Either the player was alive and was supposed to have zero vote,
						// or the player was revived and consequently loses his jury votes
					},
				},
			};

			return next;
		}

		case Game.ActionType.Upgrade: {
			if (state.type != 'started')
				return null;

			if (!oneOrMore(action.count))
				return null;

			const actor = state.users[action.actorId];

			const totalCost = action.count * state.rangeUpgradeCost;
			if (totalCost > actor.actionPoints)
				return null;

			const next: Game.PlayingState = {
				...state,
				users: {
					...state.users,
					[action.actorId]: {
						...actor,
						actionPoints: actor.actionPoints - totalCost,
						range: actor.range + action.count,
					},
				},
			};

			return next;
		}

		case Game.ActionType.Heal: {
			if (state.type != 'started')
				return null;

			if (!oneOrMore(action.count))
				return null;

			const actor = state.users[action.actorId];

			// ?? Dead can't heal even if they have action points, I guess
			if (actor.health <= 0)
				return null;

			const totalCost = action.count * state.healCost;
			if (totalCost > actor.actionPoints)
				return null;

			const next: Game.PlayingState = {
				...state,
				users: {
					...state.users,
					[action.actorId]: {
						...actor,
						actionPoints: actor.actionPoints - totalCost,
						health: actor.health + action.count,
					},
				},
			};

			return next;
		}

		case Game.ActionType.Vote: {
			if (state.type != 'started')
				return null;

			if (!oneOrMore(action.count))
				return null;

			// Can you vote for yourself?
			// If you have votes you should be dead,
			// and you can't vote for dead
			// so this condition shouldn't matter, but just in case
			if (action.actorId == action.receiverId)
				return null;

			const actor = state.users[action.actorId];
			if (actor.votesAvailable < action.count)
				return null;

			const receiver = state.users[action.receiverId];
			if (receiver.health <= 0)
				return null;

			const next: Game.PlayingState = {
				...state,
				users: {
					...state.users,
					[action.actorId]: {
						...actor,
						votesAvailable: actor.votesAvailable - action.count,
					},
					[action.receiverId]: {
						...receiver,
						votesReceived: receiver.votesReceived + action.count,
					},
				},
			};

			return next;
		}

		case Game.ActionType.TestDistributePoints: {
			if (state.type != 'started')
				return null;

			// This is test function,
			// so actionPoints and healthPoints are allowed to be negative
			if (!isInteger(action.actionPoints)
				|| !isInteger(action.healthPoints))
				return null;

			const next: Game.PlayingState = {
				...state,
				users: mapValues(
					state.users,
					u => ({
						...u,
						actionPoints: u.actionPoints
							+ (u.health > 0 || action.deadReceiveActionPoints
								? action.actionPoints
								: 0),
						health: u.health
							+ (u.health > 0 || action.deadReceiveHealthPoints
								? action.healthPoints
								: 0),
					})
				),
			};

			return next;
		}
	}
}

export function zeroOrMore(n: number) {
	return n >= 0 && isInteger(n);
}

export function oneOrMore(n: number) {
	return n >= 1 && isInteger(n);
}

export function posEqual(a: Game.Pos, b: Game.Pos): boolean {
	return a.x == b.x && a.y == b.y;
}

export function distance(a: Game.Pos, b: Game.Pos): number {
	return max(
		abs(a.x - b.x),
		abs(a.y - b.y)
	);
}